// src/utils/knownWordsBuilder.js
import { normalizeToken } from "./sentenceClozeHelpers";
import { expandWordForms } from "../data/wordForms";

export { expandWordForms };

/**
 * Baut ein KnownWords-Set aus weekly_sets + spanischen Verb-Daten.
 *
 * Annahmen:
 * - weeklySetsForCurrentConfig: Array von Rows aus weekly_sets
 *   mit mindestens: { source, type }
 *   type z.B. "verb", "noun", "phrase", ...
 *
 * - spanishVerbs: Array aus loadVerbFormsForCloze (Konjugationsdateien):
 *   { id, infinitive, "1s": ..., ..., "3p": ..., otherForms: [fui, comía, …] }
 *
 * Von bekannten Verben zählen alle Zeitformen, von den übrigen Wörtern die Formen aus
 * expandWordForms (src/data/wordForms.js: Geschlecht, Mehrzahl, Kurzformen wie "buen"),
 * nach den Regeln der Lernsprache `lang`.
 */

export function buildKnownWordsFromWeeklySetsAndVerbs(
  weeklySetsForCurrentConfig,
  spanishVerbs,
  lang = "es"
) {
  const known = new Set();

  if (!weeklySetsForCurrentConfig || weeklySetsForCurrentConfig.length === 0) {
    return known;
  }

  // 1) Alle es-Einträge aus weekly_sets als bekannte Wörter
  for (const row of weeklySetsForCurrentConfig) {
    if (!row || !row.source) continue;

    // Falls es Phrasen gibt (z.B. "tener que"), nach Wörtern splitten
    const parts = String(row.source)
      .split(/\s+/)
      .map((p) => normalizeToken(p))
      .filter(Boolean);

    for (const p of parts) {
      expandWordForms(p, row.type, lang).forEach((f) => known.add(f));
    }
  }

  // 2) Alle Verbformen, deren Infinitiv im weekly_set vorkommt
  if (spanishVerbs && spanishVerbs.length > 0) {
    // Bilde Set der im weekly_set vorkommenden Verb-Infinitive
    const verbInfinitivesInWeeklySets = new Set();
    for (const row of weeklySetsForCurrentConfig) {
      if (!row || !row.source) continue;
      if (row.type && row.type !== "verb") continue; // optional: nur Typ "verb"

      const base = normalizeToken(String(row.source));
      if (base) verbInfinitivesInWeeklySets.add(base);
    }

    for (const v of spanishVerbs) {
      if (!v || !v.infinitive) continue;

      const baseInf = normalizeToken(v.infinitive);
      if (!baseInf) continue;

      if (!verbInfinitivesInWeeklySets.has(baseInf)) {
        continue; // Dieses Verb ist (noch) nicht im Weekly-Set → skip
      }

      // Infinitiv selbst
      known.add(baseInf);

      // alle konjugierten Formen aufnehmen
      for (const key of Object.keys(v)) {
        if (key === "id" || key === "infinitive") continue;
        const value = v[key];
        if (!value) continue;

        const values = Array.isArray(value) ? value : [value];
        for (const item of values) {
          const form = normalizeToken(String(item));
          if (form) known.add(form);
        }
      }
    }
  }

  return known;
}
